import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ChevronRight, Megaphone } from 'lucide-react';
import styled from 'styled-components';
import { getMyAnnouncements, isUnopened } from '../../announcements/config';
import { formatDate } from '../../../utils/Utils';
import {
  Card,
  CardHeader,
  CardTitle,
  EmptyState,
  ScrollList,
  ListItem,
  ListItemContent,
  ListItemName,
  ListItemSub,
} from '../styles';

const ClickableHeader = styled(CardHeader)`
  cursor: pointer;
  user-select: none;
`;

const HeaderLink = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.primary.main};
  font-size: 12px;
  font-weight: 600;
`;

const Thumb = styled.div`
  width: 48px;
  height: 36px;
  border-radius: 4px;
  background: ${({ $src }) =>
    $src ? `#E5E7EB url(${$src}) center/cover no-repeat` : '#EEF2FF'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6366F1;
  flex-shrink: 0;
`;

// Açılmamış duyuru işareti (AKM POC §8): başta nokta, kalın başlık, "Yeni" rozeti.
const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const UnopenedDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.primary.main};
`;

const UnopenedBadge = styled.span`
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.primary.main};
  background: ${({ theme }) => `${theme.colors.primary.main}1A`};
`;

const MAX = 5;

export default function AnnouncementsCard() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const [items, setItems] = useState([]);

  useEffect(() => {
    getMyAnnouncements()
      .then((res) => {
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setItems(list.slice(0, MAX));
      })
      .catch(() => {});
  }, []);

  const goToList = () => history.push('/my-announcements');

  return (
    <Card $delay="0.3s">
      <ClickableHeader onClick={goToList}>
        <CardTitle>{t('dashboard.announcements')}</CardTitle>
        <HeaderLink>
          {t('common.seeAll') || t('dashboard.seeAll')}
          <ChevronRight size={14} />
        </HeaderLink>
      </ClickableHeader>
      {items.length === 0 ? (
        <EmptyState>
          <Megaphone size={28} />
          {t('dashboard.noAnnouncements')}
        </EmptyState>
      ) : (
        <ScrollList>
          {items.map((a) => {
            const unopened = isUnopened(a);
            return (
              <ListItem
                key={a.id}
                style={{ cursor: 'pointer' }}
                data-unopened={unopened ? 'true' : undefined}
                onClick={() => history.push(`/announcements/${a.id}`)}
              >
                <Thumb $src={a.imageUrl}>
                  {!a.imageUrl && <Megaphone size={18} />}
                </Thumb>
                <ListItemContent>
                  <NameRow>
                    {unopened && <UnopenedDot aria-hidden="true" />}
                    <ListItemName style={unopened ? { fontWeight: 800 } : undefined}>{a.title}</ListItemName>
                    {unopened && <UnopenedBadge>{t('announcement.unopened')}</UnopenedBadge>}
                  </NameRow>
                  <ListItemSub>
                    {a.publishedAt ? formatDate(a.publishedAt, i18n.language) : ''}
                  </ListItemSub>
                </ListItemContent>
              </ListItem>
            );
          })}
        </ScrollList>
      )}
    </Card>
  );
}
